'use client'

import React, { useCallback } from 'react'
import type { Project, ProjectCategory, ProjectArchiveBlock } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { Heading } from '@/components/ui/heading'
import { Button, buttonVariants } from '@/components/ui/Button'
import { ProjectCard } from '@/components/ProjectCard'
import { ProjectStats } from './ProjectStats'
import { Subheading } from '@/components/ui/subheading'
import { StickyFilterBar } from '@/components/StickyFilterBar'
import RichText from '@/components/RichText'
import { Text } from '@/components/ui/text'

type ProjectArchiveClientProps = {
  heading: ProjectArchiveBlock['heading']
  description: ProjectArchiveBlock['description']
  projects: Project[]
  categories: ProjectCategory[] | null
  selectedCategorySlug: string
  currentPage: number
  totalPages: number
  totalProjects: number
  stats: {
    totalStorage: number
    blockchainHeight: number
  }
}

const id = 'project-archive'

export function ProjectArchiveClient({
  stats,
  heading,
  description,
  projects,
  categories,
  selectedCategorySlug,
  currentPage,
  totalPages,
  totalProjects,
}: ProjectArchiveClientProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const resetScrollPosition = useCallback(() => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const handleCategoryChange = useCallback(
    (category: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('category', category)
      params.delete('page') // Reset to page 1 when changing category
      router.push(`/community?${params.toString()}`, { scroll: false })
      resetScrollPosition()
    },
    [router, searchParams, pathname],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('page', page.toString())
      router.push(`/community?${params.toString()}`, { scroll: false })
      resetScrollPosition()
    },
    [router, searchParams, pathname],
  )

  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  return (
    <div className="bg-olive-4">
      <div className="container mx-auto px-4 py-[50px] flex flex-col md:flex-row md:justify-between gap-[50px] md:items-center">
        <div className="flex flex-col gap-[50px]">
          <Heading size="h2" className="text-highlighter">
            {heading}
          </Heading>
          {description && <RichText data={description} className="prose-light" />}
        </div>
        <ProjectStats
          flexClassName="md:flex-row"
          eyebrow={{
            label: 'Projects',
            color: 'highlighter',
          }}
          projectCount={totalProjects}
          blockchainHeight={stats.blockchainHeight}
          totalStorage={stats.totalStorage}
        />
      </div>

      <div id={id} className="relative -top-[50px]" />

      {/* Sticky Categories Bar */}
      {categories && (
        <StickyFilterBar
          label={
            <Subheading size="sm" className="text-highlighter flex-none pt-[20px] md:pt-[0px]">
              Filter by type
            </Subheading>
          }
          className="bg-olive-4"
        >
          <div className="px-4 flex gap-2 items-center py-[10px] md:py-[15px]">
            <Button
              key="all"
              onClick={() => handleCategoryChange('all')}
              variant={selectedCategorySlug === 'all' ? 'slime' : 'ghost'}
              className="text-light font-styreneB text-[1.2rem] h-[40px]"
            >
              all
            </Button>
            {categories.map((category) => (
              <Button
                key={category.slug}
                onClick={() => handleCategoryChange(category.slug?.toString() ?? '')}
                variant={selectedCategorySlug === category.slug?.toString() ? 'slime' : 'ghost'}
                className="text-light font-styreneB text-[1.2rem] h-[40px]"
              >
                {category.title}
              </Button>
            ))}
          </div>
        </StickyFilterBar>
      )}

      <div className="bg-olive-1">
        <div className="container mx-auto px-4 py-16">
          {/* Projects grid */}
          {projects?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-[50px] mb-12 min-h-[50vh]">
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  variant="light"
                  aspectRatio="3/4"
                  imageClassName="w-full h-[200px] md:w-[120px] md:h-auto"
                />
              ))}
            </div>
          ) : (
            <div className="min-h-[400px] w-full flex items-center justify-center">
              <Text size="xl">No projects found.</Text>
            </div>
          )}

          {/* Pagination */}
          <div className="flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    disabled={!hasPrevPage}
                    onClick={() => handlePageChange(currentPage - 1)}
                  />
                </PaginationItem>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Show current page and surrounding pages
                  let pageNumber: number

                  if (totalPages <= 5) {
                    // Show all pages if 5 or fewer
                    pageNumber = i + 1
                  } else if (currentPage <= 3) {
                    // Near the start
                    pageNumber = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    // Near the end
                    pageNumber = totalPages - 4 + i
                  } else {
                    // In the middle
                    pageNumber = currentPage - 2 + i
                  }

                  return (
                    <PaginationItem key={pageNumber}>
                      <PaginationLink
                        className={cn(
                          buttonVariants({
                            variant: pageNumber === currentPage ? 'olive' : 'ghost',
                          }),
                        )}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  )
                })}

                <PaginationItem>
                  <PaginationNext
                    disabled={!hasNextPage}
                    onClick={() => handlePageChange(currentPage + 1)}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>
    </div>
  )
}
